import { Controller, FieldError, useFormContext } from "react-hook-form";
import { IInputNumber } from "./InputNumberProps";
import { FormHelperText, InputAdornment, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { FnComponents, I18n, formatNumber } from "utilities/utilities";
import { I18NextNs, RegularExpression, TypeComponent } from "enum/enum";

const InputNumberItem = (props: IInputNumber) => {
  const {
    arrStringAllow = [],
    disabled = false,
    defaultValue = null,
    id,
    IsAllowScientific = false,
    SciMessage = null,
    IsCanMinus = false,
    IsShowMessageError = true,
    placeholder,
    textAlign = "right",
    variant = "outlined",
    label = "",
    max = null,
    min = null,
    maxDigits = 5,
    maxInteger = 10,
    name,
    required,
    size = "small",
    suffix = null,
    IsLeadingZero = false,
    IsComma = true,
    validateMessage = null,
    IsOverSixMonth = true,
    IsDisplayLabel = false,
    maxLength,
  } = props;

  const [IsShrink, setIsShrink] = useState(false);
  const { control, register } = useFormContext();

  const rules = useMemo(() => {
    return {
      required: {
        value: required,
        message: `${I18n.SetText("required", I18NextNs.validation)} ${label}`,
      },
      pattern: {
        value: IsAllowScientific ? RegularExpression.NumberScientific : RegularExpression.Number,
        message: `${I18n.SetText("patternNumber", I18NextNs.validation)}`,
      },

      validate: (value) => {
        if (value) {
          let sValue = value;
          let sVal = (sValue + "").replaceAll(/,/g, "");
          sVal = sVal.replaceAll(",", "");
          if (isNumber(sVal)) {
            let nVal = +sVal;
            if (min) {
              return (
                nVal >= min
              );
            }
            if (max) {
              return (
                nVal <= max
              );
            }
          }
        }
      },
    };
  }, [required, label, IsAllowScientific, min, max]);

  const inputPropsPadding = useMemo(() => {
    let sPX = "";
    if (suffix) {
      if (suffix.length > 20) {
        sPX = `${suffix.length * 8 + 30}px`;
      }
      else if (suffix.length > 15) {
        sPX = `${suffix.length * 7 + 40}px`;
      }
      else if (suffix.length > 10) {
        sPX = `${suffix.length * 6 + 50}px`;
      }
      else if (suffix.length > 5) {
        sPX = `${suffix.length * 5 + 50}px`;
      }
      else if (suffix.length > 3) {
        sPX = `${suffix.length * 4 + 45}px`;
      }
      else {
        sPX = `${suffix.length * 3 + 40}px`;
      }
    }
    return sPX;
  }, [suffix]);

  const handleCheckInterger = (sVal: string, arrNumber: string[], nMaxInteger: number, IsScientis: boolean) => {
    let sInteger = (sVal.includes(".") ? arrNumber[0] : sVal).substring(0, nMaxInteger);

    //ป้องกันการกด 0 หลายๆตัว
    let nInteger = parseFloat(sInteger);
    if (nInteger === 0 || nInteger === -0) {
      let nVal = parseFloat(sVal);
      sInteger = ["-0", "-00", "-0.", "-00."].includes(sVal) || nVal < 0 ? "-" + nInteger : nInteger + "";
    }
    else if (nInteger > 0 || nInteger < 0) {
      //ป้องกันใส่ 0 หน้าต้วเลข
      if (!IsScientis) {
        sInteger = nInteger.toString();
      }
    }
    return sInteger;
  }

  const handleCheckNumberOrScientific = (sVal: string, IsScientis: boolean) => {
    let TargetValue = null;
    let nMaxInteger = maxInteger;
    let nMaxDigits = maxDigits;
    let arrNumber = sVal.split(".");

    if (IsCanMinus && sVal.includes("-")) {
      nMaxInteger += 1;
    }

    if (IsScientis) {
      nMaxDigits += 4;
    }

    let sInteger = handleCheckInterger(sVal, arrNumber, nMaxInteger, IsScientis);

    let sDigit = sVal.toString().includes(".") ? arrNumber[1] : "";
    sDigit = sDigit.substring(0, nMaxDigits);

    if (arrNumber.length > 1) {
      sVal = sInteger + "." + sDigit;
    } else {
      sVal = sInteger;
    }

    if (nMaxDigits === 0) {
      sVal = sInteger.replaceAll(".", "");
    }

    let nVal = parseFloat(sVal);
    if (nVal >= 0 || (IsCanMinus && nVal < 0) || (IsAllowScientific && (sVal.includes("e") || sVal.includes("E")))) {
      TargetValue = sVal;
    }
    return TargetValue;
  }

  const handleOnChange = (e, onChangeController) => {
    let sValue = e.target.value;
    let sVal = (sValue + "").replaceAll(/,/g, "");
    sVal = sVal.replaceAll(",", "");
    //เป็นตัวเลข || สามารถใส่ Scientific ได้
    let IsScientis = IsAllowScientific && (sVal.includes("e") || sVal.includes("E"));
    if ((isNumber(sVal) || (IsScientis)) && !IsLeadingZero) {
      e.target.value = handleCheckNumberOrScientific(sVal, IsScientis);
    }
    else if (arrStringAllow.length > 0 || IsAllowScientific) {
      e.target.value = sVal;
    }
    else if (IsCanMinus && sVal === "-") {
      e.target.value = "-";
    }
    else if (IsLeadingZero) {
      e.target.value = sVal;
    }
    else {
      e.target.value = sVal.substring(0, sVal.length - 1);
    }
    onChangeController(e);
  };

  const handleOnBlur = (e, onChangeController) => {
    let sValue = e.target.value;
    let sVal = (sValue + "").replaceAll(/,/g, "");
    sVal = sVal.replaceAll(",", "");

    if (arrStringAllow.length > 0) {
      let IsAllow = arrStringAllow.includes(sVal);

      if (!isNumber(sVal) && !IsAllow) {
        e.target.value = null;
        onChangeController(e);
      }
    } else if (IsAllowScientific) {
      if (!isNumber(sVal)) {
        e.target.value = null;
        onChangeController(e);
      }
    }

    if (props.onBlur) {
      props.onBlur(e.target.value, e);
    }
  };

  const genMessage = (min, max, validateMessage) => {
    let sMessage = "";
    if (min && max) {
      sMessage = validateMessage !== null ? `${validateMessage}` : `${I18n.SetText("inputMinMax", I18NextNs.validation)} ${min} ${I18n.SetText("to")} ${max} `;
    }
    else if (min && !max) {
      sMessage = validateMessage !== null ? `${validateMessage}` : `${I18n.SetText("inputMin", I18NextNs.validation)} ${min}`;
    }
    else if (!min && max) {
      let sMin = IsComma ? formatNumber(max, maxDigits) : max;
      sMessage = validateMessage !== null ? `${validateMessage}` : `${I18n.SetText("inputMax", I18NextNs.validation)} ${sMin} `;
    }
    return sMessage;
  }

  const getMessageValidate = (error: FieldError) => {
    let sMessage = "";
    if (IsShowMessageError) {
      if (error) {
        switch (error.type) {
          case "required":
            sMessage = `${I18n.SetText("required", I18NextNs.validation)} ${label}`;
            break;
          case "pattern":
            sMessage = `${I18n.SetText("patternNumber", I18NextNs.validation)}`;
            if (IsAllowScientific) {
              sMessage = SciMessage !== null ? `${SciMessage}` : `${I18n.SetText("patternScientific", I18NextNs.validation)}`;
            }
            break;
          case "validate":
            sMessage = genMessage(min, max, validateMessage);
            break;
        }
      }
    }
    return sMessage;
  };

  return (
    <Controller
      key={FnComponents.GetKey(TypeComponent.inputNumber, name)}
      name={name}
      control={control}
      rules={rules}
      shouldUnregister={true}
      defaultValue={defaultValue}
      render={({
        field: { onChange, onBlur, value, ref },
        fieldState: { invalid, error },
      }) => {
        let IsValid = invalid && !disabled;
        let helperText = getMessageValidate(error);
        return (
          <>
            <div style={{ display: "flex", position: "relative" }}>
              <TextField
                {...props}
                {...register(name)}
                type={"text"}
                id={FnComponents.GetId(TypeComponent.inputNumber, id, name)}
                // inputRef={ref} //ที่ปิดเพราะตอนที่เพิ่มข้อมูลเสร็จ , หายไป
                value={CommaSeparateNumber(value, maxDigits, props.ShowDigits, IsLeadingZero, IsComma)}
                error={error?.message != null}
                variant={variant}
                size={size}
                placeholder={placeholder}
                disabled={disabled}
                label={IsDisplayLabel === true ? "" : label}
                autoComplete={"off"}
                inputProps={{
                  maxLength: maxLength,
                  style: {
                    textAlign: textAlign,
                    paddingRight: inputPropsPadding
                  },
                }}
                InputProps={{
                  startAdornment: props.startAdornment ? (
                    <InputAdornment position="start">
                      {props.startAdornment}
                    </InputAdornment>
                  ) : null,
                  endAdornment: props.endAdornment ? (
                    <InputAdornment position="end">
                      {props.endAdornment}
                    </InputAdornment>
                  ) : null,
                }}
                InputLabelProps={
                  props.startAdornment || props.endAdornment
                    ? {
                      shrink: true,
                    }
                    : {
                      shrink: !!value || IsShrink,
                    }
                }
                onChange={(e) => {
                  handleOnChange(e, onChange);
                  if (props.onChange) {
                    props.onChange(e.target.value, e);
                  }
                }}
                onBlur={(e) => {
                  handleOnBlur(e, onChange);
                  setIsShrink(false);
                }}
                onKeyDown={(event) => {
                  if (props.onKeyDown) {
                    props.onKeyDown(event);
                  }
                }}
                onKeyUp={(event) => {
                  if (props.onKeyUp) {
                    props.onKeyUp(event);
                  }
                }}
                onFocus={() => {
                  setIsShrink(true);
                }}
                style={{
                  width: "100%",
                  ...props?.style,
                }}
              />
              {suffix ? (
                <span
                  style={{
                    fontSize: 16,
                    borderColor: "rgba(255, 255, 255, 0)",
                    backgroundColor: "rgba(255, 255, 255, 0)",
                    paddingTop: "0.77rem",
                    paddingLeft: "0.3rem",
                    lineHeight: "18px",
                    height: "100%",
                    right: "2%",
                    position: "absolute",
                    color: "#929292",
                    borderLeft: "1.2px solid #d9d9d9",
                  }}
                >
                  {suffix}
                </span>
              ) : (
                <div></div>
              )}
            </div>
            {IsShowMessageError && IsValid && (
              <FormHelperText>{helperText}</FormHelperText>
            )}
          </>
        )
      }}
    />
  );
};

export default InputNumberItem


export const isNumber = (n) => {
  return typeof n != "boolean" && !isNaN(n);
};

export const convertStringToNumber = (val) => {
  if (val !== undefined && val != null) {
    let values = (val + "").replaceAll(",", "");
    if (values && values !== "" && values != null && isNumber(values)) {
      let valConve = parseFloat(values);
      if (valConve + "" !== "NaN") {
        return valConve;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }
};

export const CommaSeparateNumber = (value: any, _MaxDigits?, _MaxInteger?, IsLeadingZero?, IsComma?) => {
  if (value && isNumber(value) && !IsLeadingZero && IsComma) {
    let indValue = (value + "").split(".");
    let valueResult = indValue[0];
    if (value + "" !== "0") {
      while (/(\d+)(\d{3})/.test(valueResult)) {
        valueResult = valueResult.replace(/(\d+)(\d{3})/, "$1,$2");
      }
      if (indValue.length > 1) {
        valueResult = valueResult + "." + indValue[1];
      }
      return valueResult;
    } else {
      return "0";
    }
  }
};

export const ConvertNumberFormatterLocale = (value, digit) => {
  let valueDecimal = null;
  const numberFormatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: digit
  });
  if (value !== "" && value != null && !isNaN(value)) valueDecimal = numberFormatter.format(value);
  return valueDecimal;
} 